import React, { useState, useEffect, useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import { trpc } from '@/lib/trpc';
import { 
  Tv, 
  Maximize2, 
  Minimize2, 
  RefreshCw, 
  PlayCircle, 
  Clock, 
  AlertTriangle, 
  CheckCircle2, 
  PauseCircle, 
  XCircle,
  Activity,
  Gauge,
  Layers,
  ArrowUpRight,
  ExternalLink,
  ChevronRight,
  Sparkles,
  Volume2,
  VolumeX,
  Wrench
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Link } from '@tanstack/react-router';

export type MachineCategory = 'ALL' | 'OFFSET' | 'LAMINATION' | 'DIECUT' | 'GLUING' | 'FINISHING';

const CATEGORY_TABS: { key: MachineCategory; label: string }[] = [
  { key: 'ALL', label: 'Tất cả' },
  { key: 'OFFSET', label: 'In Offset' },
  { key: 'LAMINATION', label: 'Cán màng' },
  { key: 'DIECUT', label: 'Bế kim' },
  { key: 'GLUING', label: 'Dán hộp' },
  { key: 'FINISHING', label: 'Hoàn thiện' },
];

export default function AndonLiveBoardPage() {
  const [selectedCategory, setSelectedCategory] = useState<MachineCategory>('ALL');
  const [isFullscreen, setIsFullscreen] = useState(false);
  const [isLiveStream, setIsLiveStream] = useState(true);
  const [currentTime, setCurrentTime] = useState(new Date());

  // Đồng hồ số chạy từng giây
  useEffect(() => {
    const timer = setInterval(() => setCurrentTime(new Date()), 1000);
    return () => clearInterval(timer);
  }, []);

  // Lắng nghe sự kiện fullscreen
  useEffect(() => {
    const handleFullscreenChange = () => {
      setIsFullscreen(!!document.fullscreenElement);
    };
    document.addEventListener('fullscreenchange', handleFullscreenChange);
    return () => document.removeEventListener('fullscreenchange', handleFullscreenChange);
  }, []);

  const [isSoundEnabled, setIsSoundEnabled] = useState(false);

  // Web Audio API còi cảnh báo Andon khẩn cấp (880Hz / 440Hz bíp ngắt quãng)
  const playAndonAlarm = () => {
    try {
      const AudioCtx = window.AudioContext || (window as any).webkitAudioContext;
      if (!AudioCtx) return;
      const ctx = new AudioCtx();
      const osc = ctx.createOscillator();
      const gain = ctx.createGain();
      osc.type = 'sawtooth';
      osc.frequency.setValueAtTime(880, ctx.currentTime);
      osc.frequency.setValueAtTime(440, ctx.currentTime + 0.15);
      gain.gain.setValueAtTime(0.12, ctx.currentTime);
      gain.gain.exponentialRampToValueAtTime(0.01, ctx.currentTime + 0.35);
      osc.connect(gain);
      gain.connect(ctx.destination);
      osc.start();
      osc.stop(ctx.currentTime + 0.35);
    } catch {}
  };

  const toggleFullscreen = () => {
    if (!document.fullscreenElement) {
      document.documentElement.requestFullscreen().catch(err => {
        console.warn('Lỗi kích hoạt fullscreen:', err);
      });
    } else {
      document.exitFullscreen().catch(err => {
        console.warn('Lỗi thoát fullscreen:', err);
      });
    }
  };

  // Nạp dữ liệu Andon từ Backend qua tRPC, polling mỗi 10 giây khi bật Live Stream
  const { data, isLoading, isFetching, refetch } = useQuery({
    ...trpc.oee.getFactoryAndonBoard.queryOptions({ category: selectedCategory }),
    refetchInterval: isLiveStream ? 10_000 : false,
    refetchOnWindowFocus: true,
  });

  const summary = data?.summary || {
    totalMachines: 0,
    filteredMachinesCount: 0,
    runningCount: 0,
    setupCount: 0,
    downCount: 0,
    idleCount: 0,
    totalQtyOk: 0,
    totalQtyNg: 0,
    totalDefectRate: 0,
  };

  const machines = data?.machines || [];

  // Xác định ca sản xuất hiện tại
  const currentHour = currentTime.getHours();
  const currentShift = useMemo(() => {
    if (currentHour >= 6 && currentHour < 14) return 'Ca 1 (06:00 - 14:00)';
    if (currentHour >= 14 && currentHour < 22) return 'Ca 2 (14:00 - 22:00)';
    return 'Ca 3 (22:00 - 06:00)';
  }, [currentHour]);

  return (
    <div className={`min-h-screen bg-slate-100/70 text-slate-900 flex flex-col font-sans transition-colors duration-300 ${isFullscreen ? 'p-6' : 'p-4 md:p-6'}`}>
      
      {/* ── 1. Top Industrial Andon Header (Minimalist Light Theme) ── */}
      {/* Mobile: tiêu đề đã có trên thanh header chung → ẩn khối tiêu đề lớn, cụm điều khiển 1 hàng cuộn ngang */}
      <header className="bg-white border border-slate-200 rounded-xl p-2.5 md:p-4 shadow-sm flex items-center justify-between gap-4">
        <div className="hidden md:flex items-center gap-3 min-w-0">
          <div className="p-2.5 bg-blue-50 border border-blue-200 rounded-xl text-blue-600">
            <Tv className="w-6 h-6" />
          </div>
          <div>
            <div className="flex items-center gap-2.5">
              <h1 className="text-xl lg:text-2xl font-black tracking-tight uppercase text-slate-900">
                BẢNG GIÁM SÁT ANDON
              </h1>
              <span className="px-2 py-0.5 rounded bg-emerald-100 text-emerald-800 font-bold border border-emerald-300 text-xs">
                {currentShift}
              </span>
            </div>
          </div>
        </div>

        {/* Cụm điều khiển Live & Đồng hồ điện tử */}
        <div className="flex items-center gap-2 w-full md:w-auto md:ml-auto overflow-x-auto hide-scrollbar flex-nowrap [&>*]:shrink-0">
          <span className="md:hidden px-2 py-1 rounded bg-emerald-100 text-emerald-800 font-bold border border-emerald-300 text-xs whitespace-nowrap">
            {currentShift}
          </span>
          {/* Live Heartbeat Indicator */}
          <div className="flex items-center gap-2 px-2.5 py-1.5 rounded-lg bg-slate-100 border border-slate-300 text-xs font-mono font-bold">
            <div className="relative flex h-2 w-2">
              {isLiveStream && (
                <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-emerald-400 opacity-75"></span>
              )}
              <span className={`relative inline-flex rounded-full h-2 w-2 ${isLiveStream ? 'bg-emerald-500' : 'bg-amber-500'}`}></span>
            </div>
            <span className={isLiveStream ? 'text-emerald-700' : 'text-amber-700'}>
              {isLiveStream ? 'LIVE' : 'TẠM DỪNG'}
            </span>
            {isFetching && <RefreshCw className="w-3 h-3 text-blue-600 animate-spin" />}
          </div>

          {/* Digital Clock */}
          <div className="px-3 py-1 rounded-lg bg-slate-900 border border-slate-800 text-right shadow-sm">
            <div className="text-base lg:text-lg font-bold font-mono text-emerald-400 tracking-wider">
              {currentTime.toLocaleTimeString('vi-VN')}
            </div>
          </div>

          {/* Action Buttons */}
          <Button
            variant="outline"
            size="sm"
            onClick={() => {
              const next = !isSoundEnabled;
              setIsSoundEnabled(next);
              if (next) playAndonAlarm();
            }}
            className={`h-8 text-xs font-bold shadow-sm transition-colors ${
              isSoundEnabled
                ? 'bg-amber-100 border-amber-400 text-amber-900 font-black'
                : 'bg-white border-slate-300 text-slate-600 hover:bg-slate-100'
            }`}
            title={isSoundEnabled ? 'Tắt còi cảnh báo Andon' : 'Bật còi cảnh báo Andon khi có sự cố'}
          >
            {isSoundEnabled ? <Volume2 className="w-3.5 h-3.5 text-amber-700 mr-1 animate-pulse" /> : <VolumeX className="w-3.5 h-3.5 mr-1 text-slate-500" />}
            <span>{isSoundEnabled ? 'Còi: BẬT' : 'Còi: TẮT'}</span>
          </Button>

          <Button
            variant="outline"
            size="sm"
            onClick={() => setIsLiveStream(!isLiveStream)}
            className="bg-white border-slate-300 hover:bg-slate-100 text-slate-800 font-medium text-xs h-8 shadow-sm"
          >
            {isLiveStream ? 'Dừng' : 'Bật Live'}
          </Button>

          <Button
            variant="outline"
            size="sm"
            onClick={() => refetch()}
            className="bg-white border-slate-300 hover:bg-slate-100 text-slate-800 font-medium text-xs h-8 shadow-sm"
          >
            <RefreshCw className="w-3.5 h-3.5 mr-1" />
            Nạp Lại
          </Button>

          <Button
            variant="outline"
            size="sm"
            onClick={toggleFullscreen}
            className="bg-white border-blue-300 hover:bg-blue-50 text-blue-700 font-medium text-xs h-8 shadow-sm"
          >
            {isFullscreen ? <Minimize2 className="w-3.5 h-3.5 mr-1" /> : <Maximize2 className="w-3.5 h-3.5 mr-1" />}
            {isFullscreen ? 'Thu Nhỏ' : 'Toàn Màn Hình'}
          </Button>

          <Link to="/mes/maintenance">
            <Button size="sm" variant="outline" className="bg-white border-amber-300 hover:bg-amber-50 text-amber-800 font-bold text-xs h-8 shadow-sm">
              <Wrench className="w-3.5 h-3.5 mr-1" />
              Bảo Trì TPM
            </Button>
          </Link>

          <Link to="/mes/oee">
            <Button size="sm" className="bg-blue-600 hover:bg-blue-700 text-white font-bold text-xs h-8 shadow-sm">
              <Gauge className="w-3.5 h-3.5 mr-1" />
              OEE
            </Button>
          </Link>
        </div>
      </header>

      {/* ── 2. High-Visibility Factory KPI Ribbon (Tối giản, số liệu thực 100%) ── */}
      <section className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-3 py-3">
        {/* Tổng số máy */}
        <div className="bg-white border border-slate-200 rounded-xl p-3 shadow-sm">
          <div className="text-[11px] uppercase tracking-wider text-slate-500 font-bold">Tổng Số Máy</div>
          <div className="text-2xl font-black text-slate-900 mt-1 flex items-baseline gap-1">
            {summary.totalMachines}
            <span className="text-xs font-normal text-slate-500">máy</span>
          </div>
        </div>

        {/* Đang chạy */}
        <div className="bg-emerald-50 border-2 border-emerald-300 rounded-xl p-3 shadow-sm">
          <div className="text-[11px] uppercase tracking-wider text-emerald-800 font-bold flex items-center gap-1.5">
            <PlayCircle className="w-3.5 h-3.5 text-emerald-600" /> Đang Chạy
          </div>
          <div className="text-2xl font-black text-emerald-700 mt-1 flex items-baseline gap-1">
            {summary.runningCount}
            <span className="text-xs font-normal text-emerald-800">máy</span>
          </div>
        </div>

        {/* Chuẩn bị */}
        <div className="bg-amber-50 border-2 border-amber-300 rounded-xl p-3 shadow-sm">
          <div className="text-[11px] uppercase tracking-wider text-amber-800 font-bold flex items-center gap-1.5">
            <Clock className="w-3.5 h-3.5 text-amber-600" /> Chuẩn Bị
          </div>
          <div className="text-2xl font-black text-amber-800 mt-1 flex items-baseline gap-1">
            {summary.setupCount}
            <span className="text-xs font-normal text-amber-800">máy</span>
          </div>
        </div>

        {/* Dừng sự cố */}
        <div className="bg-rose-50 border-2 border-rose-300 rounded-xl p-3 shadow-sm">
          <div className="text-[11px] uppercase tracking-wider text-rose-800 font-bold flex items-center gap-1.5">
            <AlertTriangle className="w-3.5 h-3.5 text-rose-600" /> Dừng Sự Cố
          </div>
          <div className="text-2xl font-black text-rose-700 mt-1 flex items-baseline gap-1">
            {summary.downCount}
            <span className="text-xs font-normal text-rose-700">máy</span>
          </div>
        </div>

        {/* Chờ việc */}
        <div className="bg-slate-100 border border-slate-300 rounded-xl p-3 shadow-sm">
          <div className="text-[11px] uppercase tracking-wider text-slate-600 font-bold flex items-center gap-1.5">
            <PauseCircle className="w-3.5 h-3.5 text-slate-500" /> Chờ Việc
          </div>
          <div className="text-2xl font-black text-slate-700 mt-1 flex items-baseline gap-1">
            {summary.idleCount}
            <span className="text-xs font-normal text-slate-500">máy</span>
          </div>
        </div>

        {/* Sản lượng ca */}
        <div className="bg-blue-50 border-2 border-blue-300 rounded-xl p-3 shadow-sm">
          <div className="text-[11px] uppercase tracking-wider text-blue-800 font-bold flex items-center gap-1.5">
            <Activity className="w-3.5 h-3.5 text-blue-600" /> Sản Lượng
          </div>
          <div className="text-2xl font-black text-blue-700 mt-1 flex items-baseline gap-1">
            {summary.totalQtyOk.toLocaleString()}
            <span className="text-xs font-normal text-blue-800">sp</span>
          </div>
        </div>
      </section>

      {/* ── 3. Category Filter Tabs (Tối giản) ── */}
      <nav className="flex flex-wrap gap-2 pb-3">
        {CATEGORY_TABS.map((tab) => (
          <button
            key={tab.key}
            onClick={() => setSelectedCategory(tab.key)}
            className={`px-3 py-1.5 rounded-lg text-xs font-medium transition-all shadow-sm ${
              selectedCategory === tab.key
                ? 'bg-blue-600 text-white font-bold border border-blue-600'
                : 'bg-white text-slate-700 hover:text-slate-900 hover:bg-slate-100 border border-slate-300'
            }`}
          >
            {tab.label}
          </button>
        ))}
      </nav>

      {/* ── 4. Smart Machine Grid Cards (Solid Borders, Zero Murky Gradients) ── */}
      {isLoading ? (
        <div className="flex-1 flex flex-col items-center justify-center py-20 text-slate-500 bg-white rounded-2xl border border-slate-200">
          <RefreshCw className="w-10 h-10 animate-spin text-blue-600 mb-3" />
          <p className="text-sm font-semibold text-slate-700">Đang tải bản đồ trạng thái sàn máy Andon...</p>
        </div>
      ) : machines.length === 0 ? (
        <div className="flex-1 flex flex-col items-center justify-center py-20 text-slate-500 bg-white border border-dashed border-slate-300 rounded-2xl">
          <Tv className="w-12 h-12 text-slate-400 mb-3" />
          <p className="text-base text-slate-700 font-bold">Không tìm thấy thiết bị nào trong phân nhóm này</p>
          <p className="text-xs text-slate-500 mt-1">Vui lòng chọn phân nhóm máy khác hoặc kiểm tra cấu hình thiết bị.</p>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 2xl:grid-cols-4 gap-4 flex-1">
          {machines.map((machine: any) => {
            const isRunning = machine.status === 'RUNNING';
            const isSetup = machine.status === 'SETUP';
            const isDown = machine.status === 'DOWN';
            const isIdle = machine.status === 'IDLE';
            const job = machine.currentJob;

            // Border và Header styling dựa theo trạng thái (Solid, High Contrast)
            let cardBorder = 'border-slate-300 bg-white';
            let headerBg = 'bg-slate-100 border-slate-200 text-slate-700';
            let statusBadge = <Badge className="bg-slate-200 text-slate-700 font-bold border-0">CHỜ VIỆC</Badge>;

            if (isRunning) {
              cardBorder = 'border-2 border-emerald-500 bg-white shadow-sm';
              headerBg = 'bg-emerald-50 border-emerald-200 text-emerald-900';
              statusBadge = <Badge className="bg-emerald-600 text-white font-bold border-0 animate-pulse">ĐANG CHẠY</Badge>;
            } else if (isSetup) {
              cardBorder = 'border-2 border-amber-400 bg-white shadow-sm';
              headerBg = 'bg-amber-50 border-amber-200 text-amber-900';
              statusBadge = <Badge className="bg-amber-500 text-slate-950 font-bold border-0">CHUẨN BỊ MÁY</Badge>;
            } else if (isDown) {
              cardBorder = 'border-2 border-rose-500 bg-white shadow-sm';
              headerBg = 'bg-rose-50 border-rose-200 text-rose-900';
              statusBadge = <Badge className="bg-rose-600 text-white font-bold border-0">SỰ CỐ / DỪNG</Badge>;
            }

            return (
              <Card key={machine.id} className={`${cardBorder} rounded-xl transition-all duration-200 flex flex-col justify-between overflow-hidden shadow-sm hover:shadow-md`}>
                <div>
                  {/* Card Header: Tên máy, mã máy và Badge trạng thái */}
                  <div className={`p-3.5 border-b ${headerBg} flex items-start justify-between gap-2`}>
                    <div>
                      <div className="flex items-center gap-2">
                        <span className="text-base lg:text-lg font-black text-slate-900 tracking-tight">
                          {machine.name}
                        </span>
                      </div>
                      <div className="flex items-center gap-2 mt-0.5 text-xs text-slate-600 font-mono">
                        <span className="font-bold text-blue-700">{machine.code}</span>
                        <span>•</span>
                        <span>{machine.departmentName}</span>
                      </div>
                    </div>
                    <div>{statusBadge}</div>
                  </div>

                  {/* Card Body: Chi tiết bài in hoặc thông tin máy */}
                  <div className="p-4 space-y-3">
                    {job ? (
                      <>
                        {/* Thông tin lệnh sản xuất & sản phẩm */}
                        <div>
                          <div className="flex items-center justify-between text-xs">
                            <span className="font-mono text-blue-700 font-bold">{job.poCode}</span>
                            <span className="text-slate-600 font-medium truncate max-w-[140px]">{job.customerName}</span>
                          </div>
                          <div className="text-sm font-bold text-slate-900 truncate mt-0.5" title={job.productName}>
                            {job.productName}
                          </div>
                          <div className="text-xs text-indigo-700 mt-1 flex items-center gap-1 font-medium">
                            <Layers className="w-3.5 h-3.5" />
                            <span>{job.stepName}</span>
                            <span className="text-slate-400">•</span>
                            <span className="text-slate-700">{job.operatorName}</span>
                          </div>
                        </div>

                        {/* Thanh Tiến Độ */}
                        <div className="space-y-1 pt-1">
                          <div className="flex justify-between text-xs font-mono">
                            <span className="text-slate-700 font-semibold">{job.completedQty.toLocaleString()} / {job.targetQty.toLocaleString()}</span>
                            <span className="text-emerald-700 font-black">{job.progressPercent}%</span>
                          </div>
                          <div className="w-full bg-slate-200 rounded-full h-2.5 overflow-hidden">
                            <div 
                              className={`h-full rounded-full transition-all duration-500 ${
                                isRunning ? 'bg-emerald-500' : 'bg-amber-500'
                              }`}
                              style={{ width: `${job.progressPercent}%` }}
                            />
                          </div>
                        </div>

                        {/* 4 Trụ Cột Sẵn Sàng */}
                        <div className="flex items-center justify-end gap-1.5 pt-2 border-t border-slate-200 text-[11px]">
                          <span className={job.fourPillars.paper ? 'px-1.5 py-0.5 rounded bg-emerald-100 text-emerald-800 border border-emerald-300 font-medium' : 'px-1.5 py-0.5 rounded bg-rose-100 text-rose-800 border border-rose-300 font-medium'}>
                            Giấy
                          </span>
                          <span className={job.fourPillars.plates ? 'px-1.5 py-0.5 rounded bg-emerald-100 text-emerald-800 border border-emerald-300 font-medium' : 'px-1.5 py-0.5 rounded bg-rose-100 text-rose-800 border border-rose-300 font-medium'}>
                            Kẽm
                          </span>
                          <span className={job.fourPillars.mold ? 'px-1.5 py-0.5 rounded bg-emerald-100 text-emerald-800 border border-emerald-300 font-medium' : 'px-1.5 py-0.5 rounded bg-rose-100 text-rose-800 border border-rose-300 font-medium'}>
                            Khuôn
                          </span>
                          <span className="px-1.5 py-0.5 rounded bg-emerald-100 text-emerald-800 border border-emerald-300 font-medium">
                            Phụ
                          </span>
                        </div>
                      </>
                    ) : (
                      /* Trường hợp máy trống IDLE hoặc DOWNTIME */
                      <div className="py-5 text-center text-slate-500 space-y-1">
                        {isDown ? (
                          <>
                            <XCircle className="w-8 h-8 text-rose-500 mx-auto" />
                            <p className="text-sm font-bold text-rose-700">Dừng máy / Sự cố</p>
                          </>
                        ) : (
                          <>
                            <Clock className="w-8 h-8 text-slate-400 mx-auto" />
                            <p className="text-sm font-bold text-slate-600">Chờ việc</p>
                          </>
                        )}
                      </div>
                    )}
                  </div>
                </div>

                {/* Card Footer: Thống kê ca và nút 1-chạm mở Touch Terminal */}
                <div className="px-4 py-2.5 bg-slate-50 border-t border-slate-200 flex items-center justify-between text-xs">
                  <div className="flex items-center gap-2 font-mono text-[11px] text-slate-600 font-medium">
                    <span>Hôm nay: <strong className="text-slate-900">{machine.todayStats.qtyOk.toLocaleString()}</strong></span>
                    <span>•</span>
                    <span className={machine.todayStats.defectRatePct > 5 ? 'text-rose-600 font-bold' : 'text-slate-600'}>
                      Phế: {machine.todayStats.defectRatePct}%
                    </span>
                  </div>

                  <div className="flex items-center gap-1.5">
                    {isDown && (
                      <Link
                        to="/mes/maintenance"
                        search={{ machineId: machine.id }}
                        className="inline-flex items-center gap-1 px-2 py-1 rounded bg-rose-50 text-rose-700 hover:bg-rose-600 hover:text-white border border-rose-300 font-bold text-xs transition shadow-sm animate-pulse"
                        title="Xem và xử lý phiếu sự cố trên Trung tâm Bảo Trì TPM"
                      >
                        <Wrench className="w-3 h-3" />
                        <span>Sự Cố</span>
                      </Link>
                    )}

                    <Link 
                      to="/mes" 
                      search={{ machineId: machine.id }}
                      className="inline-flex items-center gap-1 px-2.5 py-1 rounded bg-blue-50 text-blue-700 hover:bg-blue-600 hover:text-white border border-blue-200 font-bold text-xs transition shadow-sm"
                    >
                      <span>Vào Trạm</span>
                      <ExternalLink className="w-3 h-3" />
                    </Link>
                  </div>
                </div>
              </Card>
            );
          })}
        </div>
      )}
    </div>
  );
}
